/**
 * integratedHomeModel.js — 통합 관리 홈 계산 (2026-10-01, 시안대로) — 화면 없이 계산만 한다.
 *
 *   목록  : 정렬값 → 번호. 짝이 있는 출퇴근 달력은 짝 출퇴근 메뉴 바로 아래 "└" 줄(사이드바에선 숨김).
 *   요약  : 메뉴 수(짝 달력 포함 = DB 줄 수) · 묶인 회사(본사 먼저, 나머지 가나다) · 배정 합계(메뉴별 합 — 같은 사람을 여러 번 셈)
 *   표    : 사이드바에 보이는 메뉴 × 묶인 회사. 짝 달력은 뺀다.
 */
import { findPairedAttendanceMenu, integratedAttendanceTabs } from '../attendancehub/integratedAttendanceTabs';
import { displayMenuName } from '../adminlayout/integratedScope';
import { integratedFeatureLabel } from '../integratedmenu/integratedFeatureMeta';

const upper = (c) => String(c || '').trim().toUpperCase();
const byOrder = (a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) || Number(a.id) - Number(b.id);
const TAB_LABEL = { calendar: '[달력]', detail: '[직원별 상세]' };

/** 목록 둘째 줄 설명. pairedWith = 이 달력 메뉴가 합쳐진 출퇴근 메뉴. */
export function menuDesc(menu, pairedWith = null) {
  const head = `통합 메뉴 #${menu.id}`;
  if (pairedWith) return `${head} · ${displayMenuName(pairedWith.name)} 안 [달력] 탭으로 합쳐짐 (사이드바 숨김)`;
  const tabs = integratedAttendanceTabs(menu.feature_type);
  if (tabs.length > 0) return `${head} · ${tabs.map((t) => TAB_LABEL[t]).join('')} 탭`;
  return `${head} · ${integratedFeatureLabel(menu.feature_type)}`;
}

/** @returns {{menu, label, paired: object|null, desc}[]} */
export function homeMenuRows(menus) {
  const list = (Array.isArray(menus) ? menus : []).filter(Boolean);
  const pairOf = new Map(); // 달력 메뉴 id → 짝 출퇴근 메뉴
  for (const m of list) {
    const p = findPairedAttendanceMenu(m, list);
    if (p) pairOf.set(Number(m.id), p);
  }
  const rows = [];
  for (const m of [...list].sort(byOrder)) {
    if (pairOf.has(Number(m.id))) continue;
    rows.push({ menu: m, label: displayMenuName(m.name), paired: null, desc: menuDesc(m) });
    for (const cal of list.filter((c) => pairOf.get(Number(c.id)) === m).sort(byOrder)) {
      rows.push({ menu: cal, label: displayMenuName(cal.name), paired: m, desc: menuDesc(cal, m) });
    }
  }
  return rows;
}

/** 묶인 회사 합집합 — 본사(이 사이트의 주인 회사)를 맨 앞, 나머지는 가나다. */
export function unionCompanyCodes(menus, hqCode) {
  const set = new Set();
  for (const m of Array.isArray(menus) ? menus : []) {
    for (const c of Array.isArray(m?.company_codes) ? m.company_codes : []) if (upper(c)) set.add(upper(c));
  }
  const hq = upper(hqCode);
  return [...set].sort((a, b) => (a === hq ? -1 : 0) - (b === hq ? -1 : 0) || a.localeCompare(b));
}

export function homeSummary(menus, hqCode) {
  const rows = homeMenuRows(menus);
  return {
    menuCount: rows.length,
    sidebarCount: rows.filter((r) => !r.paired).length,
    companyCodes: unionCompanyCodes(menus, hqCode),
    assigneeSum: rows.reduce((sum, r) => sum + (Number(r.menu.assignees_count) || 0), 0),
  };
}

/** @returns {{cols: string[], rows: {menu, label, cells: {code, on}[]}[]}} */
export function homeMatrix(menus, hqCode) {
  const cols = unionCompanyCodes(menus, hqCode);
  const rows = homeMenuRows(menus).filter((r) => !r.paired).map((r) => {
    const own = new Set((Array.isArray(r.menu.company_codes) ? r.menu.company_codes : []).map(upper));
    return { menu: r.menu, label: r.label, cells: cols.map((code) => ({ code, on: own.has(code) })) };
  });
  return { cols, rows };
}
